<template>
  <div class="contractForm">
    <div class="add-content-container">
      <div class="add-titieMain">
        <div class="add-title-container">{{ pageTitle }}</div>
      </div>

      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="110px"
        size="small"
        class="contract-form-body"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="标的物类型" prop="propertyType">
              <el-select v-model="form.propertyType" placeholder="请选择" style="width:100%" disabled>
                <el-option
                  v-for="item in propertyTypeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="合同名称" prop="contractName">
              <el-input v-model="form.contractName" maxlength="100" placeholder="请输入合同名称" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="合同开始日期" prop="contractStart">
              <el-date-picker
                v-model="form.contractStart"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="选择开始日期"
                style="width:100%"
                @change="recalcEndAndRent"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="合同类型" prop="assetType">
              <el-select v-model="form.assetType" placeholder="请选择" style="width:100%">
                <el-option
                  v-for="item in assetTypeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="租赁期限" prop="leaseTermYears">
              <el-select v-model="form.leaseTermYears" placeholder="请选择" style="width:100%" @change="recalcEndAndRent">
                <el-option
                  v-for="item in leaseTermOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="承租方名称" prop="tenantId">
              <el-select
                v-model="form.tenantId"
                filterable
                clearable
                placeholder="请选择已审核通过的承租方"
                style="width:100%"
              >
                <el-option
                  v-for="item in tenantOptions"
                  :key="item.id"
                  :label="item.tenantName"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="关联标的物" required>
          <div class="prop-toolbar">
            <span class="prop-count">已选（{{ form.properties.length }}）</span>
            <el-button size="small" type="primary" plain @click="openPropertySelector" v-if="edit">
              选择标的物
            </el-button>
          </div>
          <el-table :data="form.properties" border size="small" style="width:100%">
            <i slot="empty" style="font-style:normal">请选择标的物</i>
            <el-table-column label="序号" type="index" align="center" width="60" />
            <el-table-column prop="propertyCode" label="标的物编号" align="center" min-width="120" show-overflow-tooltip />
            <el-table-column label="标的物类型" align="center" width="120">
              <template slot-scope="scope">{{ typeLabel(scope.row) }}</template>
            </el-table-column>
            <el-table-column prop="area" label="面积(亩)" align="center" width="100" />
            <el-table-column prop="address" label="地址" align="center" min-width="160" show-overflow-tooltip />
            <el-table-column label="租金(元)" align="center" width="160">
              <template slot-scope="scope">
                <el-input
                  v-model.number="scope.row.rentAmount"
                  type="number"
                  :min="0"
                  size="mini"
                  style="width:130px"
                  @change="recalcTotalRent"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" align="center" width="80" v-if="edit">
              <template slot-scope="scope">
                <span class="content-del" @click="removeProperty(scope.$index)">删除</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="prop-summary">
            <div class="prop-summary-cell">
              <span class="prop-summary-label">标的物数量</span>
              <span class="prop-summary-value">{{ form.properties.length }}</span>
            </div>
            <div class="prop-summary-cell">
              <span class="prop-summary-label">标的物面积合计(亩)</span>
              <span class="prop-summary-value">{{ formatMoney(totalPropertyArea) }}</span>
            </div>
            <div class="prop-summary-cell">
              <span class="prop-summary-label">租金合计(元)</span>
              <el-form-item prop="totalRent" class="prop-summary-form-item">
                <el-input
                  v-model.number="form.totalRent"
                  type="number"
                  :min="0"
                  size="small"
                  style="width:160px"
                />
              </el-form-item>
            </div>
          </div>
        </el-form-item>

        <el-form-item class="save-cancel">
          <el-button class="cancel-btn" @click="onClose">取消</el-button>
          <el-button
            v-if="edit"
            class="save-btn"
            :loading="saving"
            @click="onSave"
          >保存</el-button>
        </el-form-item>
      </el-form>
    </div>

    <property-selector
      ref="propertySelector"
      :exclude-ids="selectedPropertyIds"
      @confirm="onPropertiesSelected"
    />
  </div>
</template>

<script>
import moment from "moment";
import mixins from "@/common/MixIn.js";
import PropertySelector from "./PropertySelector.vue";
import {
  pageTenant,
  getContract,
  addContract,
  editContract,
  getProperty,
  leaseRequestError
} from "@/service/fishPondLease";
import {
  AssetTypeOptions,
  PropertyTypeOptions,
  SignMethod,
  ContractType,
  FeeItemType,
  PropertyType,
  enumLabel
} from "@/service/fishPondLeaseEnums";

export default {
  name: "ContractForm",
  mixins: [mixins],
  components: { PropertySelector },
  data() {
    return {
      saving: false,
      contractId: null,
      tenantOptions: [],
      assetTypeOptions: AssetTypeOptions,
      propertyTypeOptions: PropertyTypeOptions,
      leaseTermOptions: Array.from({ length: 20 }, function(_, i) {
        return { value: i + 1, label: i + 1 + "年" };
      }),
      form: this.emptyForm(),
      rules: {
        propertyType: [{ required: true, message: "请选择标的物类型", trigger: "change" }],
        contractName: [{ required: true, message: "请输入合同名称", trigger: "blur" }],
        contractStart: [{ required: true, message: "请选择合同开始日期", trigger: "change" }],
        assetType: [{ required: true, message: "请选择合同类型", trigger: "change" }],
        leaseTermYears: [{ required: true, message: "请选择租赁期限", trigger: "change" }],
        tenantId: [{ required: true, message: "请选择承租方", trigger: "change" }],
        totalRent: [{ required: true, message: "请输入租金合计", trigger: "blur" }]
      }
    };
  },
  computed: {
    pageTitle() {
      return this.contractId ? "编辑合同" : "新增合同";
    },
    selectedPropertyIds() {
      return (this.form.properties || []).map(function(p) {
        return p.propertyId;
      });
    },
    totalPropertyArea() {
      let sum = 0;
      (this.form.properties || []).forEach(function(p) {
        sum += Number(p.area) || 0;
      });
      return sum;
    }
  },
  async mounted() {
    await this.initPagePermission();
    await this.loadTenants();
    const query = this.$route.query || {};
    if (query.id) {
      this.contractId = query.id;
      await this.loadDetail(query.id);
    } else {
      if (query.tenantId) {
        this.form.tenantId = this.normalizeId(query.tenantId);
      }
      if (query.propertyId) {
        await this.prefillProperty(query.propertyId);
      }
      this.recalcEndAndRent();
    }
  },
  methods: {
    typeLabel(row) {
      var typeTxt = enumLabel("propertyType", row.type);
      if (
        Number(row.type) === PropertyType.FishPond &&
        row.subType != null &&
        row.subType !== ""
      ) {
        return typeTxt + " - " + enumLabel("propertySubType", row.subType);
      }
      return typeTxt;
    },
    formatMoney(val) {
      const n = Number(val) || 0;
      return n.toFixed(2);
    },
    emptyForm() {
      return {
        propertyType: PropertyType.FishPond,
        contractName: "",
        tenantId: null,
        assetType: 1,
        contractStart: moment().format("YYYY-MM-DD"),
        contractEnd: "",
        leaseTermYears: 1,
        totalRent: 0,
        properties: []
      };
    },
    normalizeId(val) {
      if (val === null || val === undefined || val === "") return null;
      const n = Number(val);
      if (!isNaN(n) && String(n) === String(val)) return n;
      return val;
    },
    loadTenants() {
      return pageTenant({
        pageIndex: 1,
        pageSize: 1000,
        auditStatus: 3
      })
        .then(res => {
          const data = (res && res.data) || {};
          this.tenantOptions = data.list || [];
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        });
    },
    loadDetail(id) {
      return getContract(id)
        .then(res => {
          const d = (res && res.data) || {};
          const months = d.leaseTerm || 12;
          const years = Math.max(1, Math.round(months / 12));
          const props = (d.properties || []).map(function(p) {
            return {
              propertyId: p.propertyId,
              propertyCode: p.propertyCode,
              type: p.type,
              subType: p.subType,
              area: p.area,
              address: p.address,
              rentAmount: p.rentAmount
            };
          });
          this.form = {
            propertyType: (props[0] && props[0].type) || PropertyType.FishPond,
            contractName: d.contractName || "",
            tenantId: d.tenantId,
            assetType: d.assetType,
            contractStart: d.contractStart
              ? moment(d.contractStart).format("YYYY-MM-DD")
              : "",
            contractEnd: d.contractEnd
              ? moment(d.contractEnd).format("YYYY-MM-DD")
              : "",
            leaseTermYears: years,
            totalRent: d.totalRent || 0,
            properties: props
          };
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        });
    },
    prefillProperty(propertyId) {
      return getProperty(propertyId)
        .then(res => {
          const p = (res && res.data) || {};
          if (!p.id) return;
          this.form.propertyType = p.type || this.form.propertyType;
          this.form.properties = [
            {
              propertyId: p.id,
              propertyCode: p.propertyCode,
              type: p.type,
              subType: p.subType,
              area: p.area,
              address: p.address,
              rentAmount: p.propertyRent
            }
          ];
          this.recalcTotalRent();
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        });
    },
    openPropertySelector() {
      if (this.$refs.propertySelector) {
        this.$refs.propertySelector.open();
      }
    },
    onPropertiesSelected(rows) {
      const existMap = {};
      (this.form.properties || []).forEach(function(p) {
        existMap[p.propertyId] = true;
      });
      const merged = (this.form.properties || []).slice();
      (rows || []).forEach(function(row) {
        if (!existMap[row.propertyId]) {
          merged.push(row);
        }
      });
      this.form.properties = merged;
      this.recalcTotalRent();
    },
    removeProperty(index) {
      this.form.properties.splice(index, 1);
      this.recalcTotalRent();
    },
    sumRentAmount() {
      let sum = 0;
      (this.form.properties || []).forEach(function(p) {
        sum += Number(p.rentAmount) || 0;
      });
      return sum;
    },
    recalcTotalRent() {
      const years = Number(this.form.leaseTermYears) || 1;
      this.form.totalRent = Number((this.sumRentAmount() * years).toFixed(2));
    },
    recalcEndAndRent() {
      if (this.form.contractStart && this.form.leaseTermYears) {
        this.form.contractEnd = moment(this.form.contractStart)
          .add(this.form.leaseTermYears, "years")
          .format("YYYY-MM-DD");
      } else {
        this.form.contractEnd = "";
      }
      this.recalcTotalRent();
    },
    buildPayload() {
      const years = Number(this.form.leaseTermYears) || 1;
      return {
        id: this.contractId || undefined,
        contractName: this.form.contractName,
        tenantId: this.form.tenantId,
        assetType: this.form.assetType,
        contractStart: this.form.contractStart,
        contractEnd: this.form.contractEnd,
        leaseTerm: years * 12,
        paymentInterval: 12,
        paymentPeriod: years,
        totalRent: this.form.totalRent,
        // 以下字段对齐源系统（表单未展示），给默认值
        deposit: 0,
        signMethod: SignMethod.Offline,
        contractType: ContractType.New,
        feeItemType: FeeItemType.Rent,
        properties: (this.form.properties || []).map(function(p) {
          return {
            propertyId: p.propertyId,
            rentAmount: p.rentAmount
          };
        })
      };
    },
    onSave() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        if (!this.form.properties || !this.form.properties.length) {
          this.$message.warning("请至少选择一个标的物");
          return;
        }
        this.saving = true;
        const payload = this.buildPayload();
        const req = this.contractId ? editContract(payload) : addContract(payload);
        req
          .then(() => {
            this.$message.success("保存成功");
            this.onClose();
          })
          .catch(err => {
            this.$message.error(leaseRequestError(err));
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    onClose() {
      this.$router.push({ name: "contractManage" });
    }
  }
};
</script>

<style scoped>
.contractForm >>> .add-titieMain {
  height: auto;
  min-height: 0;
}
.contract-form-body {
  padding: 16px 24px 40px;
  background: #fff;
  border: 1px solid #dddddd;
  border-top: none;
}
.save-cancel {
  margin-top: 24px;
}
.save-cancel .save-btn {
  margin-left: 20px;
}
.prop-toolbar {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.prop-count {
  margin-right: 12px;
  font-size: 14px;
  color: #606266;
}
.prop-summary {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 40px;
  margin-top: 12px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.prop-summary-cell {
  display: flex;
  align-items: center;
}
.prop-summary-label {
  font-size: 14px;
  color: #606266;
  margin-right: 8px;
  white-space: nowrap;
}
.prop-summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.prop-summary-form-item {
  margin-bottom: 0;
}
.form-tip {
  color: #999;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
}
.content-del {
  color: #f56c6c;
  cursor: pointer;
}
</style>
